import { capitalize } from "./string";

export function getDaysInMonth(year, month) {
	return new Date(year, month, 0).getDate();
}

export function getWeekdayIntl(date, locale = navigator.language) {
	const weekdayName = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
	const dayNumber = date.getDay();
	return {
		number: dayNumber,
		name: weekdayName,
	};
}

export function formatWeekDayAndHour(date) {
	const d = new Date(date);
	let weekdayName = getWeekdayIntl(d).name;
	const charIdx = weekdayName.indexOf('-');
	if (charIdx !== -1) {
		weekdayName = weekdayName.substring(0, charIdx);
	}
	return `${capitalize(weekdayName)} ${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

export const months = [
	'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
	'jul', 'ago', 'set', 'out', 'nov', 'dez'
];

export function isMoreThanOneDay(date) {
	const d = new Date(date);
	const today = new Date();
	const diff = today - d;
	const days = diff / (1000 * 60 * 60 * 23.5);
	return days > 1;
}

export function calculateDiffDays(from, to) {
	if (!from || !to) {
		return 0;
	}
	return Math.floor((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}

export function formatRelativeDate(from, to = new Date(), options = { simple: false, minDays: 7, onlyValue: false }) {
	const { simple, minDays } = options;
	const d1 = new Date(from);
	const d2 = new Date(to);

	const start = d1 < d2 ? d1 : d2;
	const end = d1 < d2 ? d2 : d1;

	let diffMs = end - start;

	const seconds = Math.floor(diffMs / 1000);
	const minutes = Math.floor(diffMs / (1000 * 60));
	const hours = Math.floor(diffMs / (1000 * 60 * 60));
	const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

	if (seconds < 60) {
		return !options.onlyValue ? (simple ? 'Agora' : 'Agora mesmo') : seconds + 's';
	}
	if (minutes < 60) {
		return simple ? `${minutes}m` : `${minutes}min atrás`;
	}
	if (hours < 24) {
		return simple ? `${hours}h ${minutes - hours * 60}m` : `${hours}h ${minutes - hours * 60}min atrás`;
	}
	if (!minDays || days < minDays) {
		return simple ? `${days}d` : `${days}d atrás`;
	}

	if (start.getFullYear() === end.getFullYear()) {
		return `${start.getDate()} ${months[start.getMonth()]}`;
	}

	return `${start.getDate()} ${months[start.getMonth()]} de ${start.getFullYear()}`;
}

export function getTimezoneOffset() {
	const now = new Date();
	const offsetMinutes = now.getTimezoneOffset();
	return offsetMinutes * 60 * 1000 * -1;
}

export function getTimezoneOffsetInHours() {
	const now = new Date();
	const offsetMinutes = now.getTimezoneOffset();
	return offsetMinutes / 60 * -1;
}

export function concatTime(value) {
	return new Date(value?.includes(':') ? value : `${value}T00:00:00`);
}

export function formatDate(date, withTime = false) {
	if (!date) return '';
	const d = date instanceof Date ? date : new Date(Date.parse(date));
	const day = String(d.getDate()).padStart(2, '0');
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const year = d.getFullYear();
	const hours = String(d.getHours()).padStart(2, '0');
	const minutes = String(d.getMinutes()).padStart(2, '0');
	return `${day}/${month}/${year}${withTime ? ` ${hours}:${minutes}` : ''}`;
}

export function addTenDays(date = Date.now()) {
	const result = new Date(date);
	result.setDate(result.getDate() + 10);
	return result;
}

export function formatDateToYYYYMMDD(date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

export function date(format, timestamp = Date.now()) {
	const jsDate = new Date(timestamp);

	const pad = (n, width = 2) => String(n).padStart(width, '0');

	const map = {
		d: pad(jsDate.getDate()), // Day of the month, 2 digits
		D: jsDate.toLocaleString('en', { weekday: 'short' }), // Mon through Sun
		j: jsDate.getDate(), // Day of the month without leading zeros
		l: jsDate.toLocaleString('en', { weekday: 'long' }), // Sunday through Saturday
		N: jsDate.getDay() === 0 ? 7 : jsDate.getDay(), // ISO numeric day (1–7)
		w: jsDate.getDay(), // Numeric day of the week (0–6)
		m: pad(jsDate.getMonth() + 1), // Month (01–12)
		n: jsDate.getMonth() + 1, // Month without leading zeros
		M: jsDate.toLocaleString('en', { month: 'short' }), // Jan through Dec
		F: jsDate.toLocaleString('en', { month: 'long' }), // January through December
		Y: jsDate.getFullYear(), // 4-digit year
		y: String(jsDate.getFullYear()).slice(-2), // 2-digit year
		H: pad(jsDate.getHours()), // 24-hour format
		G: jsDate.getHours(), // 24-hour format without leading zeros
		h: pad(jsDate.getHours() % 12 || 12), // 12-hour format
		g: jsDate.getHours() % 12 || 12, // 12-hour format without leading zeros
		i: pad(jsDate.getMinutes()), // Minutes
		s: pad(jsDate.getSeconds()), // Seconds
		a: jsDate.getHours() < 12 ? 'am' : 'pm',
		A: jsDate.getHours() < 12 ? 'AM' : 'PM',
	};

	return format.replace(/\\?([a-zA-Z])/g, (match, letter) =>
		match.startsWith('\\') ? letter : (map[letter] ?? letter)
	);
}

export function createDateFromFormat(format, dateString) {
	if (!dateString) return null;

	const map = {
		Y: '(?<Y>\\W{4})',
		y: '(?<y>\\W{2})',
		m: '(?<m>\\W{2})',
		n: '(?<n>\\W{1,2})',
		d: '(?<d>\\W{2})',
		j: '(?<j>\\W{1,2})',
		H: '(?<H>\\W{2})',
		G: '(?<G>\\W{1,2})',
		i: '(?<i>\\W{2})',
		s: '(?<s>\\W{2})',
	};

	// Monta uma regex a partir do formato
	let regexStr = format.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1');
	for (const [key, pattern] of Object.entries(map)) {
		regexStr = regexStr.replace(new RegExp(key, 'g'), pattern);
	}

	regexStr = regexStr.replace(new RegExp('W', 'g'), 'd');

	const match = dateString.match(new RegExp(`^${regexStr}$`));
	if (!match) {
		return new Date(dateString);
	}

	const parts = match.groups;

	// Extrai valores, convertendo para número
	const year = parts.Y ? +parts.Y : (parts.y ? 2000 + +parts.y : 1970);
	const month = +(parts.m || parts.n || 1) - 1;
	const day = +(parts.d || parts.j || 1);
	const hour = +(parts.H || parts.G || 0);
	const minute = +(parts.i || 0);
	const second = +(parts.s || 0);

	return new Date(year, month, day, hour, minute, second);
}

export function parseTimeWithTimezone(time) {
	const timeParseRegex =
		/^(\d{2}):(\d{2})(?::(\d{2}))?(?:([+-])(\d{2})(?::?(\d{2}))?)?$/;

	const match = time.match(timeParseRegex);

	if (!match) return null;

	const [
		,             // string completa (ignorada)
		hours,        // 1
		minutes,      // 2
		seconds = '00', // 3 (opcional)
		offsetSign,   // 5 → '+' ou '-'
		offsetHours,  // 6
		offsetMinutes = '00' // 7 (opcional)
	] = match;

	let timezone = null;
	if (offsetSign && offsetHours) {
		timezone = ((Number(offsetHours) * 60 + Number(offsetMinutes)) * 60) * (offsetSign === '-' ? -1 : 1);
	}

	return {
		hours: parseInt(hours, 10),
		minutes: parseInt(minutes, 10),
		seconds: parseInt(seconds, 10),
		timezone
	};
}

export function parseTimeWithTimezoneToString(time) {
	const timetz = parseTimeWithTimezone(time);
	return `${timetz.hours.toString().padStart(2, '0')}:${timetz.minutes.toString().padStart(2, '0')}`;
}

export function parseTimeToTimezone(time) {
	const timetz = parseTimeWithTimezone(time);
	const offset = getTimezoneOffsetInHours();
	const offsetSignal = offset > 0 ? '+' : '-';
	return `${timetz.hours.toString().padStart(2, '0')}:${timetz.minutes.toString().padStart(2, '0')}:00${offsetSignal}${Math.abs(offset).toString().padStart(2, '0')}`;
}

export function formatPeriod(inicio, fim) {
	if (!inicio || !fim) return "";

	const dStart = inicio instanceof Date ? inicio : new Date(inicio);
	const dEnd = fim instanceof Date ? fim : new Date(fim);

	if (isNaN(dStart.getTime()) || isNaN(dEnd.getTime())) return "";

	const dToday = new Date();

	const isToday = (d) =>
		d.getDate() === dToday.getDate() &&
		d.getMonth() === dToday.getMonth() &&
		d.getFullYear() === dToday.getFullYear();

	const isSameDay = (d1, d2) =>
		d1.getDate() === d2.getDate() &&
		d1.getMonth() === d2.getMonth() &&
		d1.getFullYear() === d2.getFullYear();

	const pad = (n) => String(n).padStart(2, "0");

	const hi = `${pad(dStart.getHours())}h${pad(dStart.getMinutes())}`;
	const hf = `${pad(dEnd.getHours())}h${pad(dEnd.getMinutes())}`;

	const formatDateString = (d) => {
		const day = pad(d.getDate());
		const month = pad(d.getMonth() + 1);
		const year = d.getFullYear();

		if (year !== dToday.getFullYear()) {
			return `${day}/${month}/${year}`;
		}
		return `${day}/${month}`;
	};

	const di = formatDateString(dStart);
	const df = formatDateString(dEnd);

	// Caso 1: Ambos no dia atual (hoje)
	if (isToday(dStart) && isToday(dEnd)) {
		return `${hi} até ${hf}`;
	}

	// Caso 2: Inicia hoje (dia atual) e termina em outro dia
	if (isToday(dStart) && !isToday(dEnd)) {
		return `${hi} até ${df} ${hf}`;
	}

	// Caso 3: Mesma data de início e fim, mas que não é hoje
	if (!isToday(dStart) && isSameDay(dStart, dEnd)) {
		return `${hi} até ${di} ${hf}`;
	}

	// Caso 4: Começa em um dia e termina em outro dia (nenhum sendo hoje)
	return `${di} ${hi} até ${df} ${hf}`;
}

/**
 * Verifica se uma string qualquer representa uma data válida que pode ser
 * instanciada via `new Date(string)`.
 *
 * Além dos formatos nativamente suportados pelo Date (ISO 8601, RFC 2822, etc.),
 * também reconhece formatos comuns com separadores `/`, `-` e `.` onde o dia
 * vem antes do mês (dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy), com ou sem horário.
 *
 * @param {string} value - String a ser verificada.
 * @returns {boolean} `true` se a string puder gerar um Date válido.
 *
 * @example
 * isValidDateString('2024-06-15')           // true  (ISO)
 * isValidDateString('15/06/2024')           // true  (dd/mm/yyyy)
 * isValidDateString('15/06/2024 14:30')     // true  (dd/mm/yyyy HH:mm)
 * isValidDateString('Jun 15, 2024')         // true  (RFC 2822-like)
 * isValidDateString('not a date')           // false
 * isValidDateString('')                     // false
 * isValidDateString(null)                   // false
 */
export function isValidDateString(value) {
	if (typeof value !== 'string' || !value.trim()) {
		return false;
	}

	const trimmed = value.trim();

	// 1. Tenta o parse nativo — cobre ISO 8601, RFC 2822, etc.
	const nativeParsed = Date.parse(trimmed);
	if (!isNaN(nativeParsed)) {
		// Proteção extra: garante que o Date gerado é realmente válido
		const d = new Date(trimmed);
		return !isNaN(d.getTime());
	}

	// 2. Tenta formatos dd/mm/yyyy, dd-mm-yyyy, dd.mm.yyyy (com horário opcional)
	const dmyRegex = /^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;
	const match = trimmed.match(dmyRegex);

	if (match) {
		const [, dayStr, monthStr, yearStr, hoursStr, minutesStr, secondsStr] = match;
		let year = parseInt(yearStr, 10);
		const month = parseInt(monthStr, 10);
		const day = parseInt(dayStr, 10);
		const hours = hoursStr ? parseInt(hoursStr, 10) : 0;
		const minutes = minutesStr ? parseInt(minutesStr, 10) : 0;
		const seconds = secondsStr ? parseInt(secondsStr, 10) : 0;

		// Converte ano de 2 dígitos
		if (year < 100) {
			year += 2000;
		}

		// Validação de ranges
		if (month < 1 || month > 12) return false;
		if (day < 1 || day > 31) return false;
		if (hours < 0 || hours > 23) return false;
		if (minutes < 0 || minutes > 59) return false;
		if (seconds < 0 || seconds > 59) return false;

		// Valida se o dia existe no mês/ano (ex: 31/02 é inválido)
		const d = new Date(year, month - 1, day, hours, minutes, seconds);
		return (
			d.getFullYear() === year &&
			d.getMonth() === month - 1 &&
			d.getDate() === day
		);
	}

	return false;
}